import { buildHumusformLabel, buildStandortLabel, isCheckboxTruthy, type GeomAttributField, type GeomClassDef, type LayerLabelBuilder } from '@luzula/luzula-lib';
import { FBF_PREFIX_REGEX } from '../data-model';
import Substratfeuchte from '../../assets/json/Substratfeuchte.json';

/**
 * bpweb-Vorgabe-Layer fuer den Flaechen-Editor (topologisch korrektes
 * Digitalisieren). Diese Themen sind **bpweb-fachlich** und liegen deshalb in
 * der App, nicht in der Lib: sie werden ueber den Token `GEOM_LAYER_DEFAULTS`
 * hereingereicht (siehe app-providers.ts) — jetzt als **Factory**, damit die
 * Fachwert-Kataloge live aus den bpweb-Listen kommen.
 *
 * Eine Standort-Feinbodenform wird ueber ihre **Bausteine** eingegeben:
 * Vorsatz (`vor`) + FBF (`bofo`) + Nachsatz (`nachsatz`) + Wasserform (`wst`),
 * dazu tiefer Go (`tgo`, schreibt "(G)"). Daraus wird **LBFO automatisch
 * zusammengesetzt** (`deriveAttributes`), das Feld ist im Formular
 * schreibgeschuetzt. Zusaetzlich Substrat: `STGR` (kombinatorisch, frei),
 * `SUB` (Liste Substrattypengruppen), `sub_feu` (Substratfeuchte) und `STO_ANT`.
 * Humusform traegt `HUFO`/`HU_ANT` (1..3); HUFO wird gegen die Humusform-Regel
 * geprueft.
 */

/** Minimaler Live-Listen-Zugriff (DataService.getHardcoded) — als Interface, damit testbar. */
export interface GeomLayerListSource {
	getHardcoded(table: string): unknown[];
}

/** Humus-Wert-Pruefung (BohrpunktGpkgAutoStyleHandler.isValidHumusValue). */
export interface HumusValidator {
	isValidHumusValue(value: string): boolean;
}

// Substratfeuchte.json ist eine Matrix (X/B/C… -> je 3 Codes). Der erlaubte
// Wertevorrat = die darin vorkommenden Codes (flach, dedupliziert, sortiert).
const SUBSTRATFEUCHTE_WERTE: string[] = Array.from(
	new Set(
		Object.values(Substratfeuchte as Record<string, string[]>)
			.flat()
			.map((s) => String(s).trim())
			.filter(Boolean),
	),
).sort();

const praefixRegExp = new RegExp(FBF_PREFIX_REGEX);
/** Gueltiger Vorsatz (Praefix-Regel aus data-model). Bei kaputter Regex nicht einschraenken. */
function isValidPraefix(v: string): boolean {
	try {
		return praefixRegExp.test(v);
	} catch {
		return true;
	}
}

/** Klartext-Werte einer Live-Liste aus einer Objekt-Spalte (z.B. Feinbodenformen.FBF). */
function listColumn(lists: GeomLayerListSource, table: string, column: string): string[] {
	return lists
		.getHardcoded(table)
		.map((e) => (e as Record<string, unknown>)?.[column])
		.filter((v): v is string | number => v != null && String(v).trim() !== '')
		.map((v) => String(v));
}

/** Standort-Fachfelder je Feinbodenform 1..3 (Bausteine; LBFO wird abgeleitet). */
function standortFelder(lists: GeomLayerListSource): GeomAttributField[] {
	const out: GeomAttributField[] = [];
	for (let i = 1; i <= 3; i++) {
		const gruppe = `Feinbodenform ${i}`;
		out.push(
			{ key: `vor${i}`, label: 'Vorsatz', gruppe, validate: isValidPraefix },
			{ key: `bofo${i}`, label: 'FBF', gruppe, optionen: () => listColumn(lists, 'Feinbodenformen', 'FBF') },
			{ key: `nachsatz${i}`, label: 'Nachsatz', gruppe, optionen: () => listColumn(lists, 'BodenformSuffixe', 'value') },
			{ key: `wst${i}`, label: 'Wasserform', gruppe },
			{ key: `tgo${i}`, label: 'tiefer Go', gruppe, kind: 'checkbox' },
			{ key: `LBFO${i}`, label: 'LBFO', gruppe, readonly: true },
			{ key: `STGR${i}`, label: 'STGR', gruppe },
			{ key: `SUB${i}`, label: 'SUB', gruppe, optionen: () => lists.getHardcoded('Substrattypengruppen').map((s) => String(s)) },
			{ key: `sub_feu${i}`, label: 'Substratfeuchte', gruppe, optionen: () => SUBSTRATFEUCHTE_WERTE },
			{ key: `STO_ANT${i}`, label: 'STO_ANT', kind: 'number', gruppe },
		);
	}
	return out;
}

/**
 * LBFO_i = Vorsatz + FBF + Nachsatz + Wasserform (+ "(G)" bei tiefem Go). Wird
 * beim Speichern der Fachwerte angewandt; leere Ergebnisse entfernt die Lib.
 */
function composeLBFO(attr: Record<string, unknown>): Record<string, unknown> {
	const g = (k: string): string => {
		const v = attr[k];
		return v == null ? '' : String(v).trim();
	};
	const out: Record<string, unknown> = {};
	for (let i = 1; i <= 3; i++) {
		const core = g(`vor${i}`) + g(`bofo${i}`) + g(`nachsatz${i}`) + g(`wst${i}`);
		out[`LBFO${i}`] = core ? core + (isCheckboxTruthy(attr[`tgo${i}`]) ? '(G)' : '') : '';
	}
	return out;
}

/** Humusform-Fachfelder: bis zu 3 Humusformen, je HUFO (gegen Humus-Regel) + HU_ANT. */
function humusformFelder(humus: HumusValidator): GeomAttributField[] {
	const out: GeomAttributField[] = [];
	for (let i = 1; i <= 3; i++) {
		const gruppe = `Humusform ${i}`;
		out.push(
			{ key: `HUFO${i}`, label: 'HUFO', gruppe, validate: (v) => humus.isValidHumusValue(v) },
			{ key: `HU_ANT${i}`, label: 'HU_ANT', kind: 'number', gruppe },
		);
	}
	return out;
}

/** Volles Standort-Autostyle-Label (Feinbodenformen, mehrzeilig) als Zeilen. */
const standortLabel: LayerLabelBuilder = (attr) => {
	const l = buildStandortLabel(attr as Record<string, unknown>);
	return l ? [l.text, l.text2, l.text3].filter((s): s is string => !!s) : null;
};

/** Volles Humusform-Autostyle-Label (HUFO/HU_ANT, mehrzeilig) als Zeilen. */
const humusformLabel: LayerLabelBuilder = (attr) => {
	const l = buildHumusformLabel(attr as Record<string, unknown>);
	return l ? [l.text, l.text2].filter((s): s is string => !!s) : null;
};

/**
 * Baut die festen Vorgabe-Layer von bpweb (Standort/Humusform) fuer
 * GEOM_LAYER_DEFAULTS. Die Fachwert-Kataloge werden als **Closures** hinterlegt
 * und erst zur Nutzungszeit aus den (dann geladenen) Listen gelesen — kein
 * Ladeproblem beim App-Start.
 */
export function buildBpwebGeomLayers(lists: GeomLayerListSource, humus: HumusValidator): GeomClassDef[] {
	return [
		{
			id: 'standort',
			kind: 'area',
			displayName: 'Standort',
			// Nicht von selbst in der Ebenenliste, nur ueber „Ebenen hinzufuegen".
			nurAufWunsch: true,
			farbe: '#cc0000',
			nurUmrandung: true,
			// Durchgezogene rote Linie, ohne Versatz — sie ist die Bezugslinie.
			linienstil: 'solid',
			styleAttribute: 'STGR1',
			attributfelder: standortFelder(lists),
			pflichtfelder: [],
			// Zeile 1 = Feinbodenform-Namen, Zeile 2 = Substrat (betont/groesser), Zeile 3 = Anteile.
			labelBuilder: standortLabel,
			labelEmphasisIndex: 1,
			deriveAttributes: composeLBFO,
			topologie: {
				keineSelfIntersection: true,
				keinOverlapZuKlassen: ['standort'],
				overlapSeverity: 'hard',
				minFlaecheQm: 1.0,
			},
		},
		{
			id: 'humusform',
			kind: 'area',
			displayName: 'Humusform',
			nurAufWunsch: true,
			farbe: '#228B22',
			nurUmrandung: true,
			// Gerissen: laengere Striche, weitere Luecken als das Standardmuster ('6 4'),
			// und um genau eine Linienbreite versetzt — so bleibt eine deckungsgleiche
			// Standort-Grenze daneben sichtbar.
			linienstil: 'gerissen',
			strichmuster: '10 6',
			versatzLinienbreiten: 1,
			styleAttribute: 'HUFO1',
			attributfelder: humusformFelder(humus),
			pflichtfelder: [],
			labelBuilder: humusformLabel,
			topologie: {
				keineSelfIntersection: true,
				keinOverlapZuKlassen: ['humusform'],
				overlapSeverity: 'hard',
				minFlaecheQm: 1.0,
			},
		},
	];
}
